import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import * as savedArtworkActions from '@redux/modules/savedArtwork'

import defaultFormState, { requiredFields } from '@models/savedArtwork'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { SavedArtworkModel, SavedArtworkRequestOptions } from '@models/types'

import { launchModal } from '@domain-group/javascript-utils'

const stringifyObjectKeys: (keyof SavedArtworkModel)[] = ['data']

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (savedArtwork?: Partial<SavedArtworkModel>, options: GenerateUrlsOptions = {}) => {
  const { id } = savedArtwork || {}
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    savedArtworksIndexUrl: `#/${prefixRoute}/savedArtworks`,
    editSavedArtworkUrl: `#/${prefixRoute}/savedArtworks/${id}/edit/`,
  }
}

type CreateSavedArtworkParams = {
  dispatch: AppDispatch,
  requestOptions?: SavedArtworkRequestOptions,
  savedArtworkParams: Partial<SavedArtworkModel>,
}

const createSavedArtwork = (params: CreateSavedArtworkParams) => {
  const { dispatch, savedArtworkParams, requestOptions } = params
  const { createSavedArtwork: createFn } = savedArtworkActions

  const updatedParams = mergeAndStringifyEntityObjects({}, savedArtworkParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateSavedArtworkParams = {
  dispatch: AppDispatch,
  requestOptions?: SavedArtworkRequestOptions,
  savedArtwork: SavedArtworkModel,
  savedArtworkParams: Partial<SavedArtworkModel>,
}

const updateSavedArtwork = (params: UpdateSavedArtworkParams) => {
  const {
    dispatch, savedArtwork, savedArtworkParams, requestOptions,
  } = params
  const { updateSavedArtwork: updateFn } = savedArtworkActions

  const updatedParams = mergeAndStringifyEntityObjects(savedArtwork, savedArtworkParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

export function useRelations(savedArtwork: SavedArtworkModel) {
  const { agencies, savedArtworkPages, users } = useSelector(reduxState => reduxState.entities)

  const { agency_id, first_page, user_id } = savedArtwork

  const agency = agencies[agency_id] || {}
  // @ts-expect-error TS2538
  const firstSavedArtworkPage = savedArtworkPages[first_page] || {}
  const user = users[user_id] || {}

  return {
    agency,
    firstSavedArtworkPage,
    user,
  }
}

type DeleteSavedArtworkParams = {
  dispatch: AppDispatch,
  savedArtwork: DeleteParams<SavedArtworkModel>,
}

const deleteSavedArtwork = (params: DeleteSavedArtworkParams) => {
  const { dispatch, savedArtwork } = params
  const { deleteSavedArtwork: deleteFn } = savedArtworkActions

  return dispatch(deleteFn(savedArtwork))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useSavedArtworkForm(
  savedArtwork: Partial<SavedArtworkModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const savedArtworkForm = useForm(
    defaultFormState,
    { entity: savedArtwork, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [savedArtwork.id, savedArtwork.cache_key],
  )

  return {
    ...savedArtworkForm,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: SavedArtworkRequestOptions,
}

function useSavedArtwork(initEntity: Partial<SavedArtworkModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: savedArtwork }: { entity: SavedArtworkModel} = useLatestEntity(initEntity, 'savedArtworks')
  const { id } = savedArtwork

  const { callbacks } = useContext(PageContext)

  const { agency, firstSavedArtworkPage, user } = useRelations(savedArtwork)

  const dispatch = useDispatch()

  useReduxAction(
    'savedArtworks',
    'loadSavedArtwork',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.savedArtworks)

  return {
    agency,
    callbacks: {
      createSavedArtwork: (
        savedArtworkParams: Partial<SavedArtworkModel>,
        entityOptions?: SavedArtworkRequestOptions,
      ) => (
        createSavedArtwork({ savedArtworkParams, dispatch, requestOptions: entityOptions })
      ),
      deleteSavedArtwork: () => deleteSavedArtwork({ dispatch, savedArtwork }),
      launchManageSavedArtworkModal: () => launchModal({
        callbacks,
        modalKey: 'ManageSavedArtworkModal',
        payload: { savedArtwork },
      }),
      updateSavedArtwork: (
        savedArtworkParams: Partial<SavedArtworkModel>,
        entityOptions?: SavedArtworkRequestOptions,
      ) => (
        updateSavedArtwork({
          savedArtwork, savedArtworkParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    firstSavedArtworkPage,
    loading: loading && !loadedIds.includes(savedArtwork.id),
    savedArtwork,
    updating,
    urls: generateUrls(savedArtwork),
    user,
  }
}

export default useSavedArtwork
